import dayjs from 'dayjs';
import { ISourceStore } from '@stores/source';
import { IDestinationStore } from '@stores/destination';
import { RetlConnectionStore } from '@stores/retlConnections/retlConnection';
import { ApiResponse } from '@components/sources/sourceResponse';

export enum TabValue {
  LATEST = 'latest',
  PAST = 'past',
}

export enum SyncConfigStatus {
  STARTING = 'starting',
  SYNCING = 'in progress',
  STOPPING = 'stopping',
  PAUSED = 'paused',
  AVAILABLE_TO_SYNC = 'available_to_sync',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
  ABORTED = 'aborted',
  IDLE = 'idle',
}

export enum SyncType {
  INCREMENTAL = 'incremental',
  FULL = 'full',
}

export type DateRange = { from: dayjs.Dayjs; to: dayjs.Dayjs };

export interface ILatestSyncStore {
  loadingSyncData: boolean;
  loadingSyncTrend: boolean;
  latestSync: SyncData | null;
  syncConfigStatus: SyncConfigStatus;
  syncTrend: SyncTrend | null;
  errorInSyncTrend: boolean;
  isEligibleForGraph: boolean;
  isGraphAvailable: boolean;
  load: () => void;
  stopPolling: () => void;
  startSync: (syncType: SyncType) => void;
  stopSync: () => void;
  refreshSyncTrend: () => void;
  checkConnectionStatus: () => void;
  reset: () => void;
}

export interface IPastSyncsStore {
  loading: boolean;
  dateRange: DateRange;
  pageNumber: number;
  totalRuns: number;
  pastSyncs: SyncData[];
  onDateRangeChange: (dateRange: DateRange) => void;
  onPageNumberChange: (pageNumber: number) => void;
  load: () => void;
  reset: () => void;
}

export interface IRetlSyncsStore {
  source: ISourceStore | undefined;
  destination: IDestinationStore | undefined;
  connection: RetlConnectionStore | undefined;
  syncMode: string;
  tab: TabValue;
  latestSyncStore: ILatestSyncStore;
  pastSyncsStore: IPastSyncsStore;
  selectedSync: SyncData | null;
  sampleErrorsStore: ISampleErrorsStore | null;
  setTab: (tab: TabValue) => void;
  setConnectionDetails: (sourceId: string, destinationId: string) => void;
  setSampleErrorStore: (sync: SyncData, type: ErrorType) => void;
  resetSampleErrorStore: () => void;
  reset: () => void;
}

export interface SyncData extends Omit<IJobRunReport, 'status' | 'started_at' | 'finished_at'> {
  status:
    | SyncConfigStatus.SUCCEEDED
    | SyncConfigStatus.FAILED
    | SyncConfigStatus.ABORTED
    | SyncConfigStatus.SYNCING;
  duration: string;
  started_at: dayjs.Dayjs;
  finished_at: dayjs.Dayjs;
}

export interface RawSyncTrend {
  timestamps: number[];
  states: {
    succeeded: number[];
    retries: {
      '429': number[];
      '5xx': number[];
    };
    aborted: number[];
    dropped: number[];
  };
}

export enum GraphTabValue {
  SYNCED = 'synced',
  RETRYING = 'retrying',
}

export interface SyncTrendTabData {
  chartData: {
    bucket: number;
    charts: (number | null)[];
  }[];
  labels: string[];
  stepSize: number;
}

export interface SyncTrend {
  [GraphTabValue.SYNCED]: SyncTrendTabData;
  [GraphTabValue.RETRYING]: SyncTrendTabData;
}

export enum ErrorType {
  INVALID = 'sources',
  FAILED = 'server',
  ABORTED = 'aborted',
}

export type InvalidRow = Record<string, unknown>;

export type SampleError = {
  count: number;
  statusCode: number;
  reportedBy: string;
  reportedAt: number;
  state?: string;
};

export interface SampleErrorsConfig {
  errorType: ErrorType;
  runId: string;
  startedAt: dayjs.Dayjs;
  // When the run reached a terminal state. Bounds how long counts keep re-reading reporting
  // while it flushes its minute-buckets; optional because not every host surface has it.
  finishedAt?: dayjs.Dayjs;
  sourceId: string;
}

export interface ISampleErrorsStore {
  loading: boolean;
  config: SampleErrorsConfig;
  sampleErrors: SampleError[];
  fetchSyncErrors: () => void;
}

// Layer 1 — per-error-code counts, derived from GET /reports/metrics (unconditional, no PII gate).
export type ErrorGroup = {
  code: string | null;
  count: number;
};

// undefined = no filter (all codes), null = the no-code (pre-delivery) bucket, string = a specific code.
export type FailedRecordsCodeFilter = string | null | undefined;

// Layer 2 — a single row of the failed-records list.
export type FailedRecordRow = {
  rudderId: number;
  primaryKey: string;
  operation: string;
  errorCode: string | null;
  errorMessage: string;
};

export type FailedRecordsListResult =
  | {
      status: 'ok';
      rows: FailedRecordRow[];
    }
  | {
      status: 'not_retained';
    };

export type FailedRecordsListStatus = 'idle' | 'loading' | 'ok' | 'not_retained' | 'error';

// Layer 3 — a single drilled-in source row. This is a distinct wire contract from the Layer 2
// list — a successful row is reported as `{name,value}` pairs, not `{columns:null, rows}`. That
// asymmetry is intentional (frozen), not an oversight — do not build a rows-based mapper here.
type FailedRecordRowColumn = {
  name: string;
  value: string;
};

export type FailedRecordRowResult =
  | { status: 'ok'; columns: FailedRecordRowColumn[] }
  | { status: 'snapshot_rotated' }
  | { status: 'row_absent' };

export type FailedRecordRowStatus =
  | 'idle'
  | 'loading'
  | 'ok'
  | 'snapshot_rotated'
  | 'row_absent'
  | 'error';

export type FailedRecordsApiError = {
  code: string;
  message: string;
  reason?: string;
};

export interface FailedRecordsStoreConfig {
  connectionId: string;
  sourceId: string;
  jobRunId: string;
  startedAt: dayjs.Dayjs;
  // When the run reached a terminal state. Bounds how long counts keep re-reading reporting
  // while it flushes its minute-buckets; optional because not every host surface has it.
  finishedAt?: dayjs.Dayjs;
  failedTotal: number;
  connection: RetlConnectionStore | undefined;
}

export interface IFailedRecordsStore {
  config: FailedRecordsStoreConfig;

  canFetchRecords: boolean;
  isOptedIn: boolean;

  loadingCounts: boolean;
  countsLoaded: boolean;
  countsFailed: boolean;
  // Render the tiles skeleton rather than an empty row: reporting has published nothing yet but
  // a refresh is still expected.
  countsAwaitingFirstGroups: boolean;
  countsError: FailedRecordsApiError | null;
  errorGroups: ErrorGroup[];
  fetchCounts: () => Promise<void>;
  // Re-reads reporting on the page's existing latest-sync poll tick, so a freshly terminal
  // run's tiles fill in as reporting flushes instead of freezing at whatever was published
  // when the panel mounted. No-op once the payload stops changing.
  refreshCounts: () => Promise<void>;
  retryCounts: () => Promise<void>;

  codeFilter: FailedRecordsCodeFilter;
  searchInput: string;
  page: number;
  listStatus: FailedRecordsListStatus;
  listResult: FailedRecordsListResult | null;
  // Answered from the prefetched next page, not from the current page's row count — see the
  // store's `hasNextPage` for why a single page cannot answer it.
  hasNextPage: boolean;
  listError: FailedRecordsApiError | null;
  // Derived from the debounced/settled search, not the immediate `searchInput` — so the footer
  // (which reads totalRecords, itself keyed off the settled search) never disagrees with the
  // list actually on screen during the debounce window.
  isSearchActive: boolean;
  // The trimmed, settled search text — what the currently-rendered list/empty-state was
  // actually queried with. Distinct from `searchInput`, which can be ahead of it by up to the
  // debounce window.
  settledSearchQuery: string;
  // null while a search is active (no server-reported total for a substring match); otherwise
  // the matching ErrorGroup count for the active codeFilter.
  totalRecords: number | null;
  fetchList: () => Promise<void>;
  setCodeFilter: (code: FailedRecordsCodeFilter) => void;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;

  rowStatus: FailedRecordRowStatus;
  rowResult: FailedRecordRowResult | null;
  rowError: FailedRecordsApiError | null;
  openRow: (rudderId: number) => void;
  closeRowDrawer: () => void;

  setCanFetchRecords: (canFetch: boolean) => void;
  dispose: () => void;
}

export enum JobRunResult {
  Succeeded = 'succeeded',
  Failed = 'failed',
  Running = 'running',
}

export interface IJobRunReport {
  run_id: string;
  started_at: string;
  finished_at: string;
  snapshot_finished_at?: string;
  trigger: 'manual' | 'scheduled';
  sync_type: 'full' | 'incremental';
  metrics: {
    total: number;
    invalid: {
      total: number;
      duplicates: number;
      nulls: number;
    };
    changed: {
      total: number; // for file-based: if we receive -1 from backend means query timed out cannot fetch the data
      inserts: number;
      updates: number;
      deletes: number;
    };
    successful: {
      total: number;
      inserts: number;
      updates: number;
      deletes: number;
    };
    failed: {
      total: number;
    };
    dropped?: {
      total: number;
    };
  };
  file_stats?: {
    total_count_status: 'pending' | 'completed' | 'failed' | 'timeout';
    total_rows: number;
    total_files: number;
    changed_files: number;
    rows_processed: number;
    breakup: FileInfo[];
  };
  error?: string;
  state: string; // a descriptive text for the user to see the job run updates
  status: JobRunResult;
}

export interface FileInfo {
  total_count_status: 'pending' | 'completed' | 'failed' | 'timeout';
  file_name: string;
  status: string;
  total_rows?: number;
  rows_processed?: number;
  rows_succeeded?: number;
  rows_failed?: number;
  rows_dropped?: number;
}

export type JobRunReportsResponse = ApiResponse<{ runs: IJobRunReport[]; total_runs: number }>;
